/**
 * @lines98/client/lib/mode-registry — single source of truth for the LOBBY's mode
 * catalog (extra-modes §5.1, fix-cycle T-39R-B1 / P5 redesign).
 *
 * The lobby («Играть») groups game modes by FAMILY. Each entry declares its
 * identity, family, label, the volume-of-hints LEVEL it previews, whether it is
 * locked (not yet playable) and the «Скоро» badge text for locked entries. The
 * lobby screen + the mode-card thumbnail read this list — NOT hardcoded copies —
 * so adding a future mode = one entry here (no layout rewrite). This is the
 * «ствол → ветки» discipline (CLAUDE.md): the registry is the trunk, the lobby a
 * thin branch.
 *
 * `hintLevel` drives the board-view thumbnail (mode-card): how much of `next` the
 * preview shows — exactly the §2.1 visibility matrix (predict: ghosts+niche;
 * classic: niche only; blind: nothing). Locked entries still declare a hintLevel
 * for a representative preview, but a click is a no-op (AC: never starts a game).
 *
 * Spec: extra-modes-alltime.md §2.1 (hint matrix) + §5.1 (lobby families) +
 * AC-1.x. Locked competitive / paid-meta modes are owner-fixed placeholders
 * (geymplej не реализуем — brief Проход B): «Соревновательный режим» (на время /
 * гонка с призраком) + платный мета-режим, all «Скоро».
 */

import type { ContentLang } from '@lines98/shared/config/seo';
import type { GameMode } from '@lines98/shared/types/game';

import { t } from './i18n/index.js';

/**
 * The hint level a card's preview thumbnail renders (= the §2.1 `next`-visibility
 * matrix). For LOCKED modes that aren't one of the three core `GameMode`s, the
 * level only picks a representative preview look.
 */
export type HintLevel = 'predict' | 'classic' | 'blind';

/** Mode-family grouping shown as a section header in the lobby (§5.1). */
export type ModeFamily = 'casual' | 'competitive' | 'meta';

export interface ModeEntry {
  /** Stable id. Playable entries use the canonical `GameMode`; placeholders a slug. */
  readonly id: string;
  /** Family section the card lives under. */
  readonly family: ModeFamily;
  /** Card title — must-visible label (§0.B). */
  readonly label: string;
  /** Short hint-volume summary — must-visible label (≤ ~10 words, §0.B). */
  readonly hint: string;
  /** Preview thumbnail look (= §2.1 visibility). */
  readonly hintLevel: HintLevel;
  /** Locked = not yet playable; card muted + badge, click is a no-op. */
  readonly locked: boolean;
  /** Badge text for locked cards (e.g. «Скоро»). Undefined for unlocked. */
  readonly badge?: string;
  /**
   * The playable `GameMode` started on click. Present ONLY for unlocked entries —
   * `POST /game/start { mode }` (§7). Absent for locked placeholders.
   */
  readonly mode?: GameMode;
  /**
   * Leaderboard-only entry: the records picker shows it as a selectable board, but
   * the LOBBY skips it (it's a ranking surface, not a playable mode). Сейчас НИ ОДНА
   * запись не использует этот флаг — «Мастер-ювелир» (карат-борд) убран из видимого
   * лидерборда owner-directive 2026-06-25 (борд-по-каратам — отдельная гем-мета,
   * дремлет: серверный эндпоинт `/meta/leaderboard/master-jeweler` цел, карат-валюта/
   * бейджи gated `showKarat=false`). Флаг сохранён в типе для будущих ranking-витрин.
   */
  readonly leaderboardOnly?: boolean;
}

/**
 * Human-readable family section titles (lobby headers). Лейблы локализованы через
 * `t()` ЛЕНИВО (геттеры): значение резолвится в момент чтения `FAMILY_LABEL[fam]`,
 * а не на import — поэтому смена языка отражается без пере-импорта модуля (экраны-
 * потребители перерисовываются на locale-change и читают актуальный перевод).
 * API-форма (`Record<ModeFamily,string>` с индекс-доступом) сохранена для всех
 * потребителей (lobby / leaderboard).
 */
export const FAMILY_LABEL: Record<ModeFamily, string> = {
  get casual(): string {
    return t('game.lobby.familyCasual');
  },
  get competitive(): string {
    return t('game.lobby.familyCompetitive');
  },
  get meta(): string {
    return t('game.lobby.familyMeta');
  },
};

/**
 * Canonical lobby badge text for not-yet-available modes. Локализован через `t()`;
 * НЕ присваивается статически в `badge:` записях (это заморозило бы язык на import) —
 * вместо этого записи имеют ленивый геттер `get badge()` (см. MODE_REGISTRY ниже).
 */
function soonBadge(): string {
  return t('game.lobby.soonBadge');
}

/**
 * The lobby catalog (single source). Order within a family is owner-fixed:
 *   casual:      Стандарт → Классика → Вслепую (playable now).
 *   competitive: «На время» → «Гонка с призраком» (locked «Скоро»).
 *   meta:        платный мета-режим (locked «Скоро» placeholder).
 *
 * Hint labels for the three core modes are the canonical prototype `VARIANT_SUB`
 * texts (extra-modes §5.2). Locked entries carry a short teaser hint.
 */
export const MODE_REGISTRY: readonly ModeEntry[] = [
  // ── Обычная игра (unlocked, playable) ───────────────────────────────────────
  {
    id: 'predict',
    family: 'casual',
    label: 'Стандарт',
    get hint(): string {
      return t('game.lobby.hint.predict');
    },
    hintLevel: 'predict',
    locked: false,
    mode: 'predict',
  },
  {
    id: 'classic',
    family: 'casual',
    label: 'Классика',
    get hint(): string {
      return t('game.lobby.hint.classic');
    },
    hintLevel: 'classic',
    locked: false,
    mode: 'classic',
  },
  {
    id: 'blind',
    family: 'casual',
    label: 'Вслепую',
    get hint(): string {
      return t('game.lobby.hint.blind');
    },
    hintLevel: 'blind',
    locked: false,
    mode: 'blind',
  },
  // ── Соревновательный режим (locked «Скоро») ──────────────────────────────────
  {
    id: 'time-attack',
    family: 'competitive',
    label: 'На время',
    get hint(): string {
      return t('game.lobby.hint.timeAttack');
    },
    hintLevel: 'predict',
    locked: true,
    get badge(): string {
      return soonBadge();
    },
  },
  {
    id: 'ghost-race',
    family: 'competitive',
    label: 'Гонка с призраком',
    get hint(): string {
      return t('game.lobby.hint.ghostRace');
    },
    hintLevel: 'predict',
    locked: true,
    get badge(): string {
      return soonBadge();
    },
  },
  // ── Мета-режим ───────────────────────────────────────────────────────────────
  // «Мастер-ювелир» (борд по каратам) УДАЛЁН из видимого каталога owner-directive
  // 2026-06-25 — карат-рейтинг убран из лидерборда (отдельная гем-мета, дремлет).
  {
    id: 'meta-season',
    family: 'meta',
    label: 'Сезонный режим',
    get hint(): string {
      return t('game.lobby.hint.metaSeason');
    },
    hintLevel: 'classic',
    locked: true,
    get badge(): string {
      return soonBadge();
    },
  },
];

/**
 * Локализованные ИМЕНА режимов (AC-S3, figures-start-fixes) — единый источник на
 * id. Карточка показывает ПЕРЕВОДИМЫЙ text-узел (НЕ запечён в PNG), язык = язык
 * страницы; переключатель языка стартовой меняет имя. `label` выше — ru-дефолт
 * (исторический must-visible label); тут — параллельные переводы по языкам.
 *
 * Ключ внешнего объекта = id записи MODE_REGISTRY; значение = имя на каждый
 * CONTENT_LANG. Добавление языка = добавить колонку (как content/i18n/<lang>.json).
 * tr/id/th-имена добавлены 2026-06-25 (L0a) — это короткие factual-лейблы режимов
 * (как эндонимы языков), а не body-copy; нативные эквиваленты стандартны.
 */
export const MODE_LABEL_I18N: Readonly<Record<string, Readonly<Record<ContentLang, string>>>> = {
  predict: {
    ru: 'Стандарт',
    en: 'Standard',
    de: 'Standard',
    es: 'Estándar',
    fr: 'Standard',
    it: 'Standard',
    pt: 'Padrão',
    ja: 'スタンダード',
    ko: '스탠다드',
    vi: 'Tiêu chuẩn',
    'zh-CN': '标准',
    'zh-TW': '標準',
    tr: 'Standart',
    id: 'Standar',
    th: 'มาตรฐาน',
  },
  classic: {
    ru: 'Классика',
    en: 'Classic',
    de: 'Klassik',
    es: 'Clásico',
    fr: 'Classique',
    it: 'Classica',
    pt: 'Clássico',
    ja: 'クラシック',
    ko: '클래식',
    vi: 'Cổ điển',
    'zh-CN': '经典',
    'zh-TW': '經典',
    tr: 'Klasik',
    id: 'Klasik',
    th: 'คลาสสิก',
  },
  blind: {
    ru: 'Вслепую',
    en: 'Blind',
    de: 'Blind',
    es: 'A ciegas',
    fr: 'À l’aveugle',
    it: 'Alla cieca',
    pt: 'Às cegas',
    ja: 'ブラインド',
    ko: '블라인드',
    vi: 'Không gợi ý',
    'zh-CN': '盲玩',
    'zh-TW': '盲玩',
    tr: 'Körleme',
    id: 'Buta',
    th: 'ปิดตา',
  },
  'time-attack': {
    ru: 'На время',
    en: 'Time attack',
    de: 'Auf Zeit',
    es: 'Contrarreloj',
    fr: 'Contre la montre',
    it: 'A tempo',
    pt: 'Contra o tempo',
    ja: 'タイムアタック',
    ko: '타임 어택',
    vi: 'Tính giờ',
    'zh-CN': '限时挑战',
    'zh-TW': '限時挑戰',
    tr: 'Zamana karşı',
    id: 'Lawan waktu',
    th: 'แข่งกับเวลา',
  },
  'ghost-race': {
    ru: 'Гонка с призраком',
    en: 'Ghost race',
    de: 'Geisterrennen',
    es: 'Carrera fantasma',
    fr: 'Course contre le fantôme',
    it: 'Gara col fantasma',
    pt: 'Corrida contra o fantasma',
    ja: 'ゴーストレース',
    ko: '고스트 레이스',
    vi: 'Đua với bóng ma',
    'zh-CN': '幽灵竞速',
    'zh-TW': '幽靈競速',
    tr: 'Hayalet yarışı',
    id: 'Balapan hantu',
    th: 'แข่งกับเงา',
  },
  'meta-season': {
    ru: 'Сезонный режим',
    en: 'Seasonal mode',
    de: 'Saisonmodus',
    es: 'Modo de temporada',
    fr: 'Mode saisonnier',
    it: 'Modalità stagionale',
    pt: 'Modo sazonal',
    ja: 'シーズンモード',
    ko: '시즌 모드',
    vi: 'Chế độ theo mùa',
    'zh-CN': '赛季模式',
    'zh-TW': '賽季模式',
    tr: 'Sezon modu',
    id: 'Mode musiman',
    th: 'โหมดตามฤดูกาล',
  },
};

/**
 * Имя режима на ЯЗЫКЕ страницы (AC-S3). Берёт перевод из MODE_LABEL_I18N (единый
 * источник); если перевода нет — fallback на `entry.label` (ru-дефолт). Карточка/
 * лобби зовут это, НЕ читают `label` напрямую, чтобы имя локализовалось.
 */
export function modeLabel(entry: ModeEntry, lang: ContentLang): string {
  return MODE_LABEL_I18N[entry.id]?.[lang] ?? entry.label;
}

/** Families in lobby display order (only families that have entries). */
export const LOBBY_FAMILIES: readonly ModeFamily[] = ['casual', 'competitive', 'meta'];

/** Entries of a family, in registry order (ALL — used by the records picker). */
export function entriesOfFamily(family: ModeFamily): readonly ModeEntry[] {
  return MODE_REGISTRY.filter((e) => e.family === family);
}

/**
 * Entries of a family for the LOBBY — excludes `leaderboardOnly` ranking surfaces.
 * The lobby lists playable modes only; the records picker uses `entriesOfFamily`
 * (which keeps any leaderboard-only entries). Сейчас leaderboard-only записей нет
 * (карат-борд убран 2026-06-25) — функции тождественны, но контракт сохранён.
 */
export function lobbyEntriesOfFamily(family: ModeFamily): readonly ModeEntry[] {
  return MODE_REGISTRY.filter((e) => e.family === family && !e.leaderboardOnly);
}
